import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BsChatLeftDotsFill, BsFillBarChartFill, BsFillGridFill, BsPersonCircle, BsQuestionCircle, BsClipboardDataFill, BsFillPeopleFill } from 'react-icons/bs';
import { isMobile, isTablet } from 'react-device-detect';
import { setAdmin } from '../../reducers/UserReducer/UserActions';
import { clearCompetitiveInsightsData } from '../../reducers/CompetitiveInsightsReducer/CompetitiveInsightsActions';
import { linkNavigationBar, slideDrawer } from '../../reducers/UIReducer/UIActions';
import { FiLogOut } from 'react-icons/fi';
import useDataSuite from '../../AppMain/DataSuite/useDataSuite';

const useLinks = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const profileOpen = useSelector(({ui}) => ui.profileSlideDrawer);
  // const notificationsOpen = useSelector(({ui}) => ui.notificationsSlideDrawer);
  const helpOpen = useSelector(({ui}) => ui.helpSlideDrawer);
  const chainData = useSelector(({user}) => user.chainData);
  const emergingData = useSelector(({user}) => user.emergingData);
  const dashboard = useSelector(({user}) => user.dashboard);
  const itemScore = useSelector(({user}) => user.itemScore);
  const detailsDrawerOpen = useSelector(({ui}) => ui.datasuiteWidgetDetailsSlideDrawer);
  const isArrowstream = localStorage.getItem('branding') === 'ARROWSTREAM';
  const isBEP = localStorage.getItem('branding') === 'BEP';
  const { pathname } = location;
  const [dashboardActive, setDashboardActive] = useState(false);
  const [salesStreamActive, setSalesStreamActive] = useState(false);
  const [reportsActive, setReportsActive] = useState(false);
  const { clearDetailsFilter } = useDataSuite.useDataSuite();

  const dataSuiteAccess = chainData || emergingData;

  const closeDetailsDrawer = () => {
    if (detailsDrawerOpen) {
      dispatch(slideDrawer.subtractDatasuiteDetails());
      clearDetailsFilter();
    }
  }

  const toggleProfile = () => {
    if (profileOpen) {
      dispatch(slideDrawer.subtractProfile());
    } else {
      dispatch(slideDrawer.addProfile());
      dispatch(slideDrawer.subtractHelp());
      dispatch(slideDrawer.subtractNotifications());
    }
  }

  const toggleHelp = () => {
    if (helpOpen) {
      dispatch(slideDrawer.subtractHelp());
    } else {
      dispatch(slideDrawer.addHelp());
      dispatch(slideDrawer.subtractProfile());
      dispatch(slideDrawer.subtractNotifications());
    }
  }

  // const openNotifications = () => {
  //   if (notificationsOpen) {
  //     dispatch(slideDrawer.subtractNotifications());
  //   } else {
  //     dispatch(slideDrawer.addNotifications());
  //     dispatch(slideDrawer.subtractProfile());
  //     dispatch(slideDrawer.subtractHelp());
  //   }
  // }

  const onClickReportsActive = () => {
    setReportsActive(true);
    setDashboardActive(false);
    setSalesStreamActive(false);
  };

  const onClickDashboardActive = () => {
    setDashboardActive(true);
    setReportsActive(false);
    setSalesStreamActive(false);
  };

  const onClickSalesStreamActive = () => {
    setSalesStreamActive(true); 
    setReportsActive(false);
    setDashboardActive(false);
  };

  const closeMobileMenu = () => {
    if (isMobile) {
      dispatch(slideDrawer.subtractMobileMenu());
    }
  };

  const goToSettings = () => {
    navigate('/settings');
  };

  const goToChangeManufacturer = () => {
    dispatch(clearCompetitiveInsightsData());
    dispatch(setAdmin(true));
    navigate('/admin-login');
  };

  const goToHelp = () => {
    navigate('/help');
  };

  const logOut = () => {
    navigate('/logout');
  };

  const moveNavBar = () => {
    if(!(isMobile && !isTablet)){
      const tabSelected = document.getElementById('linkSelected');
      if(tabSelected) {
        dispatch(linkNavigationBar.setLink(tabSelected.clientWidth, tabSelected.getBoundingClientRect().left));
      }
    }
  };

  const links = [];

  const reportingLinks = {
    label: 'Reports',
    icon: () => { return (<BsClipboardDataFill />)},
    isActive: reportsActive,
    onclick: () => { onClickReportsActive(); },
    links: [
      { label: "Recent Reports",
        isActive: pathname?.toLowerCase().includes('recentreports'),
        link: '/#/recentreports',
        onclick: () => { onClickReportsActive(); closeMobileMenu(); }
      },
      { label: "All Reports",
        isActive: pathname?.toLowerCase().includes('allreports'),
        link: '/#/allreports',
        onclick: () => { onClickReportsActive(); closeMobileMenu(); }
      },
      { label: "Created By Me",
        isActive: pathname?.toLowerCase().includes('myreports'),
        link: '/#/myreports',
        onclick: () => { onClickReportsActive(); closeMobileMenu(); }
      },
      { label: "Favorites",
        isActive: pathname?.toLowerCase().includes('favoritereports'),
        link: '/#/favoritereports',
        onclick: () => { onClickReportsActive(); closeMobileMenu(); }
      },
    ],
  };

  if(isBEP) {
    const dashboardLinks = {
      label: 'Dashboard',
      icon: () => { return (<BsFillGridFill/>) },
      isActive: dashboardActive,
      onclick: () => onClickDashboardActive(),
      isDisabled: !dashboard,
      defaultOpened: true,
      links: [
        { label: 'Dashboard',
          isActive: pathname?.toLowerCase().includes('dashboard'),
          link: '/#/dashboard' ,
          onclick: () => { onClickDashboardActive(); closeMobileMenu(); }
        },
        { label: 'Campaign Management',
          isActive: pathname?.toLowerCase().includes('campaignmanagement'),
          link: '/#/campaignmanagement' ,
          onclick: () => { onClickDashboardActive(); closeMobileMenu(); }
        },
      ],
    };
    links.push(dashboardLinks);
    links.push(reportingLinks);
  }

  if(isArrowstream) {
    const salesStreamLinks = {
      label: 'SalesStream',
      icon: () => { return (<BsFillBarChartFill/>)},
      isActive: salesStreamActive,
      onclick: () => { onClickSalesStreamActive(); },
      isDisabled: !dataSuiteAccess,
      defaultOpened: true,
      links: [
        { label: "Where You're Winning",
          isActive: pathname?.toLowerCase().includes('salesstream'),
          link: '/#/salesstream',
          onclick: () => {closeDetailsDrawer(); onClickSalesStreamActive(); closeMobileMenu(); }
        },
        { label: "Growth Opportunities",
          isActive: pathname?.toLowerCase().includes('growthopportunities'),
          link: '/#/growthopportunities',
          onclick: () => {closeDetailsDrawer(); onClickSalesStreamActive(); closeMobileMenu(); }
        },
        { label: "Match Opportunity Identifier",
          isActive: pathname?.toLowerCase().includes('matchopportunityidentifier'),
          link: '/#/matchopportunityidentifier',
          onclick: () => {closeDetailsDrawer(); onClickSalesStreamActive(); closeMobileMenu(); }
        },
      ],
    };
    if (itemScore) {
      salesStreamLinks.links.push({
        label: "Item Score",
        isActive: pathname?.toLowerCase().includes('itemscore'),
        link: '/#/itemscore',
        onclick: () => {closeDetailsDrawer(); onClickSalesStreamActive(); closeMobileMenu(); }
      });
    }
    links.push(salesStreamLinks);
    links.push(reportingLinks);
  };

  const closeChatbotWindow = () => {
    var chatbotIframe = document.getElementById("chatbotIframe");
    var chatbotContainer = document.getElementById("chatbotContainer");
    var minimizedChatbotElement = document.getElementById("minimizedChatbotContainer");
    if (chatbotContainer) {
        chatbotContainer.style.visibility = 'hidden';
        chatbotIframe.style.visibility = 'hidden';
        minimizedChatbotElement.style.visibility = 'visible';
    }
  }

  const handleChatbotClose = (event) => {
    if (event.data === 'closeChatbotWindow') {
      closeChatbotWindow()
    }
  }

  window.addEventListener('message', handleChatbotClose);

  const toggleChatbot = () => {
    var chatbotIframe = document.getElementById("chatbotIframe");
    var chatbotContainer = document.getElementById("chatbotContainer");
    var minimizedChatbotElement = document.getElementById("minimizedChatbotContainer");
    if (chatbotContainer) {
      if (chatbotContainer.style.visibility === 'visible') {
        chatbotContainer.style.visibility = 'hidden';
        chatbotIframe.style.visibility = 'hidden';
        minimizedChatbotElement.style.visibility = 'visible';
      } else {
        chatbotContainer.style.visibility = 'visible';
        chatbotIframe.style.visibility = 'visible';
        minimizedChatbotElement.style.visibility = 'hidden';
      }
    }
  }

  const bottomLinks = [
    {
      label: 'Chat with Erni (Beta)',
      icon: () => { return (<BsChatLeftDotsFill/>) },
      onclick: () => toggleChatbot(),
    },
    // {
    //   label: 'Notifications',
    //   icon: () => { return (<BsBell/>) },
    //   isActive:  notificationsOpen,
    //   onclick: () => openNotifications(),
    // },
    {
      label: 'Profile',
      icon: () => { return (<BsPersonCircle/>) },
      isActive: profileOpen,
      onclick: () => toggleProfile(),
    },
    {
      label: 'User Management',
      icon: () => { return (<BsFillPeopleFill/>) },
      isActive: pathname?.includes('/usermanagement'),
      link: '/#/usermanagement',
      onclick: () => closeDetailsDrawer()
    },
    {
      label: 'Help',
      icon: () => { return (<BsQuestionCircle/>) },
      isActive: helpOpen,
      onclick: () => toggleHelp(),
    },
    {
      label: 'Logout',
      icon: () => { return (<FiLogOut/>) },
      isActive: pathname?.toLowerCase().includes('logout'),
      link: '/#/logout',
    },
  ];
  
  return {
    goToSettings, goToChangeManufacturer, logOut, goToHelp, moveNavBar, toggleChatbot,
    links: links,
    bottomLinks: bottomLinks,
  };
};


// eslint-disable-next-line
export default {
  useLinks
};
